"use strict";
Object.defineProperty(exports, "__esModule", { value: true });
exports.AutomationRowMatchError = exports.AUTOMATION_AUTO_MATCH_CHAIN = void 0;
exports.planAutomationOutputTargets = planAutomationOutputTargets;
exports.decideAutomationRowMatch = decideAutomationRowMatch;
/**
 * How an automation writes each of its output tables: the write mode, how
 * the row it writes is found and which mappings fill which column. Pure: the
 * platform keeps the plans as output targets (config sync), the local run of
 * `sl-streamkit` writes its tables by them.
 */
const readiness_1 = require("./readiness");
/** The search modes `auto` tries, narrow to broad; the first that finds rows decides. */
exports.AUTOMATION_AUTO_MATCH_CHAIN = [
    'exact',
    'starts_with',
    'contains',
];
function matchPlan(config, defaults, notFoundBehavior, stealBehavior) {
    return {
        targetTableId: config.targetTableId,
        multiMatchBehavior: config.multiMatchBehavior ?? defaults.multiMatchBehavior,
        notFoundBehavior,
        stealBehavior,
        keys: config.keys.map((key, sortOrder) => ({
            columnId: key.columnId,
            sourceField: key.sourceField,
            matchMode: (key.matchMode || defaults.matchMode),
            sortOrder,
        })),
    };
}
/** The write plan of each output table of an automation, in its order. */
function planAutomationOutputTargets(config) {
    const matchConfigs = new Map((config.matchConfigs ?? []).map(matchConfig => [
        matchConfig.id,
        matchConfig,
    ]));
    return (config.outputTables ?? []).map(table => {
        // The processed row is written directly: no row matching.
        const updatesSource = (0, readiness_1.updatesSourceRow)(config, table);
        const rowMatchConfig = !updatesSource && table.matchConfigId
            ? matchConfigs.get(table.matchConfigId)
            : undefined;
        const matches = !!rowMatchConfig && rowMatchConfig.keys.length > 0;
        let mode;
        if (updatesSource)
            mode = 'update_source';
        else if (table.sourceArrayField)
            mode = 'append_each';
        else if (!matches)
            mode = 'append';
        else
            mode =
                (rowMatchConfig.notFoundBehavior ?? 'create') === 'fail'
                    ? 'update_by_match'
                    : 'upsert';
        const matchKeyColumnIds = new Set((rowMatchConfig?.keys ?? []).map(key => key.columnId));
        const mappings = (config.outputMappings ?? [])
            .filter(mapping => mapping.tableId === table.tableId &&
            !matchKeyColumnIds.has(mapping.targetColumnId))
            .map((mapping) => {
            const relationConfig = mapping.matchConfigId
                ? matchConfigs.get(mapping.matchConfigId)
                : undefined;
            return {
                sourceField: mapping.fixedValue ? '' : (mapping.responseField ?? ''),
                targetColumnId: mapping.targetColumnId,
                fixedValue: mapping.fixedValue ?? null,
                matchConfigId: relationConfig ? relationConfig.id : null,
                lookup: relationConfig && relationConfig.keys.length > 0
                    ? matchPlan(relationConfig, { multiMatchBehavior: 'ask', matchMode: 'exact' }, null, relationConfig.stealBehavior ?? null)
                    : null,
            };
        });
        return {
            table,
            mode,
            rowMatch: matches
                ? matchPlan(rowMatchConfig, { multiMatchBehavior: 'fail', matchMode: 'auto' }, rowMatchConfig.notFoundBehavior ?? null, null)
                : null,
            mappings,
        };
    });
}
/** A write the match rules of an output table refuse. */
class AutomationRowMatchError extends Error {
    constructor() {
        super(...arguments);
        this.name = 'AutomationRowMatchError';
    }
}
exports.AutomationRowMatchError = AutomationRowMatchError;
/**
 * What an `update_by_match` or `upsert` target writes for what its keys found:
 * the rows it updates, a new row, or nothing (`asked` where a person decides).
 * The platform writer and the local run of `sl-streamkit` both follow it.
 * Throws AutomationRowMatchError where the rules refuse the write.
 */
function decideAutomationRowMatch(input) {
    const { mode, multiMatchBehavior, tableId, found } = input;
    const candidates = found.candidates;
    if (candidates) {
        switch (multiMatchBehavior) {
            case 'fail':
                throw new AutomationRowMatchError(`Ambiguous match: found ${found.matchCount ?? candidates.length} rows matching in table ${tableId}. Configure "If multiple found" to handle this case.`);
            case 'update_all':
                return { write: 'update', rowIds: candidates };
            case 'update_first':
                return candidates.length
                    ? { write: 'update', rowIds: [candidates[0]] }
                    : { write: 'none', asked: false };
            case 'skip':
                return { write: 'none', asked: false };
            case 'ask':
                return { write: 'none', asked: true };
        }
    }
    if (found.rowId)
        return { write: 'update', rowIds: [found.rowId] };
    if (mode === 'update_by_match')
        throw new AutomationRowMatchError(`update_by_match: No matching row found in table ${tableId}`);
    return { write: 'append' };
}
//# sourceMappingURL=output-targets.js.map